import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, { endpoints } from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Configure axios defaults
  // Note: Token management is now handled by the centralized API instance
  // No need to manually set axios headers here as utils/api.js handles this

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedAdmin = localStorage.getItem('admin');
      
      if (storedToken && storedAdmin) {
        try {
          const parsedAdmin = JSON.parse(storedAdmin);
          
          // Validate token format (basic JWT structure check)
          if (storedToken.split('.').length === 3) {
            // Set state - API instance will handle token automatically
            setToken(storedToken);
            setAdmin(parsedAdmin);
            
            console.log('Authentication restored from localStorage');
          } else {
            throw new Error('Invalid token format');
          }
        } catch (error) {
          console.error('Error parsing stored auth data:', error);
          // Clear corrupted data
          localStorage.removeItem('token');
          localStorage.removeItem('admin');
          localStorage.removeItem('refresh_token');
          setToken(null);
          setAdmin(null);
        }
      } else {
        // No stored auth data
        setToken(null);
        setAdmin(null);
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post(endpoints.auth.login, {
        email,
        password
      });

      if (response.data.success) {
        const { access_token, refresh_token, admin } = response.data.data;
        
        localStorage.setItem('token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('admin', JSON.stringify(admin));
        
        setToken(access_token);
        setAdmin(admin);
        
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      return { success: false, message: errorMessage };
    }
  };

  const loginSubAdmin = async (email, password) => {
    try {
      const response = await apiClient.post(endpoints.subAdmin.login, {
        email,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('admin', JSON.stringify(user));
        
        setToken(token);
        setAdmin(user);
        
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('admin');
    // Note: API instance will handle token removal automatically
  };

  const isAuthenticated = () => {
    return !!token && !!admin;
  };

  const isSuperAdmin = () => {
    return admin?.role === 'super_admin';
  };

  const isLimitedAdmin = () => {
    return admin?.role === 'limited_admin';
  };

  const value = {
    admin,
    token,
    loading,
    login,
    loginSubAdmin,
    logout,
    isAuthenticated,
    isSuperAdmin,
    isLimitedAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};